import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { formatRatePercent } from "@bw/shared/types";
import { formatMoney } from "@bw/shared/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  campaignServiceContext,
  createCampaign,
  getCampaignAuthoringContext,
  getCampaigns,
  updateCampaignStatus,
  type CampaignCreate,
  type CampaignResponse,
} from "@/libs/campaignsApi";
import { PromotionStudioDialog } from "./promotion-studio-dialog";

function presentation(
  campaign: CampaignResponse,
  serviceReady: boolean | null | undefined,
): { status: string; discovery: string } {
  const setup = {
    court_reservation: "court reservation",
    open_play: "drop-in",
    classes: "classes & clinics",
    coaching: "coaching and lesson pricing",
  }[campaign.service];
  if (campaign.status === "ended" || new Date(campaign.ends_at) <= new Date()) {
    return { status: "Ended", discovery: "No longer shown" };
  }
  if (campaign.status === "paused") {
    return { status: "Paused", discovery: "Hidden while paused" };
  }
  if (new Date(campaign.starts_at) > new Date()) {
    return {
      status: "Scheduled",
      discovery: serviceReady
        ? "Scheduled for Player discovery"
        : `Scheduled · waiting for ${setup} setup`,
    };
  }
  if (!serviceReady) {
    return {
      status: "Waiting for setup",
      discovery: `Will appear after ${setup} setup`,
    };
  }
  return { status: "Live", discovery: "Visible in Player discovery" };
}

function schedule(campaign: CampaignResponse, timeZone: string): string {
  const format = new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  });
  return `${format.format(new Date(campaign.starts_at))} – ${format.format(new Date(campaign.ends_at))}`;
}

function audience(campaign: CampaignResponse): string {
  return campaign.audience === "new_to_facility"
    ? "New to this facility"
    : campaign.audience === "new_to_service"
      ? "New to this service"
      : campaign.audience === "lapsed_to_service"
        ? `Returning after ${campaign.inactive_for_days ?? "—"} days away`
        : campaign.audience === "earned_referral"
          ? `Verified friend referral · ${campaign.per_player_limit} reward${campaign.per_player_limit === 1 ? "" : "s"} each`
          : campaign.audience === "earned_loyalty"
            ? `${campaign.reward_threshold ?? "—"} verified services per reward · ${campaign.per_player_limit} max`
            : `All players · ${campaign.per_player_limit} use${campaign.per_player_limit === 1 ? "" : "s"} each`;
}

function serviceLabel(service: CampaignResponse["service"]): string {
  return {
    court_reservation: "Court reservations",
    open_play: "Drop-in",
    classes: "Classes & clinics",
    coaching: "Private coaching",
  }[service];
}

function offer(campaign: CampaignResponse) {
  const benefit =
    campaign.discount_type === "fixed_amount"
      ? `${formatMoney(campaign.discount_amount, campaign.currency)} off`
      : `${formatRatePercent(campaign.discount_percent, 2)} off`;
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {benefit}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {audience(campaign)}
        {campaign.maximum_discount != null
          ? ` · Up to ${formatMoney(campaign.maximum_discount, campaign.currency)}`
          : ""}
        {campaign.minimum_cart_amount != null
          ? ` · Min. ${formatMoney(campaign.minimum_cart_amount, campaign.currency)}`
          : ""}
      </Typography>
    </Stack>
  );
}

export function PromotionsWorkspace() {
  const { facility, facilityId, isLoading } = useResolvedFacility();
  if (isLoading || !facilityId) {
    return (
      <Stack sx={{ px: { xs: 2, lg: 3 } }} role="status">
        <Typography>Loading promotions…</Typography>
        <Skeleton height={100} />
      </Stack>
    );
  }
  return (
    <CampaignWorkspace
      key={facilityId}
      facilityId={facilityId}
      facilityName={facility?.name ?? ""}
    />
  );
}

function CampaignWorkspace({
  facilityId,
  facilityName,
}: {
  facilityId: string;
  facilityName: string;
}) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));
  const queryClient = useQueryClient();
  const campaignsKey = ["facility-campaigns", facilityId];
  const contextKey = ["facility-campaign-authoring-context", facilityId];
  const campaignsQ = useQuery({
    queryKey: campaignsKey,
    queryFn: () => getCampaigns(facilityId),
    retry: false,
  });
  const contextQ = useQuery({
    queryKey: contextKey,
    queryFn: () => getCampaignAuthoringContext(facilityId),
    retry: false,
  });
  const [studioOpen, setStudioOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] =
    useState<CampaignResponse | null>(null);
  const [endCandidate, setEndCandidate] = useState<CampaignResponse | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const createM = useMutation({
    mutationFn: (body: CampaignCreate) => createCampaign(facilityId, body),
  });
  const statusM = useMutation({
    mutationFn: ({
      campaignId,
      status,
    }: {
      campaignId: string;
      status: "active" | "paused" | "ended";
    }) => updateCampaignStatus(facilityId, campaignId, { status }),
  });
  const campaigns = campaignsQ.data ?? [];
  const configuredServices =
    contextQ.data?.services.filter((item) => item.currency != null) ?? [];
  const waitingServices = configuredServices.filter(
    (item) => !item.service_ready,
  );

  const replaceCampaign = (saved: CampaignResponse) => {
    queryClient.setQueryData<CampaignResponse[]>(campaignsKey, (previous) => [
      ...(previous ?? []).filter(
        (campaign) => campaign.campaign_id !== saved.campaign_id,
      ),
      saved,
    ]);
  };

  const create = async (body: CampaignCreate) => {
    const saved = await createM.mutateAsync(body);
    if (saved.facility_id !== facilityId) {
      throw new Error("The promotion response did not match this facility.");
    }
    replaceCampaign(saved);
    setStudioOpen(false);
    setDuplicateSource(null);
    setNotice("Promotion published.");
    return saved;
  };

  const changeStatus = async (
    campaign: CampaignResponse,
    status: "active" | "paused" | "ended",
  ) => {
    setActionError(null);
    try {
      const saved = await statusM.mutateAsync({
        campaignId: campaign.campaign_id,
        status,
      });
      replaceCampaign(saved);
      const earned =
        campaign.audience === "earned_referral" ||
        campaign.audience === "earned_loyalty";
      setNotice(
        status === "paused"
          ? earned
            ? "Reward earning paused. Existing grants remain usable until their recorded expiry."
            : "Promotion paused. Existing bookings and receipts are unchanged."
          : status === "ended"
            ? earned
              ? "Reward earning ended. Existing grants remain usable until their recorded expiry."
              : "Promotion ended. Existing bookings and receipts are unchanged."
            : "Promotion resumed.",
      );
    } catch (cause) {
      setActionError(
        cause instanceof Error
          ? cause.message
          : "The promotion status could not be changed.",
      );
    }
  };

  const actions = (campaign: CampaignResponse) => (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {campaign.status === "active" ? (
        <Button
          size="small"
          startIcon={<PauseRoundedIcon />}
          aria-label={`Pause ${campaign.name}`}
          disabled={statusM.isPending}
          onClick={() => void changeStatus(campaign, "paused")}
        >
          Pause
        </Button>
      ) : campaign.status === "paused" ? (
        <Button
          size="small"
          aria-label={`Resume ${campaign.name}`}
          disabled={statusM.isPending}
          onClick={() => void changeStatus(campaign, "active")}
        >
          Resume
        </Button>
      ) : null}
      <Button
        size="small"
        startIcon={<ContentCopyRoundedIcon />}
        aria-label={`Duplicate ${campaign.name}`}
        onClick={() => {
          setDuplicateSource(campaign);
          setStudioOpen(true);
        }}
      >
        Duplicate
      </Button>
      {campaign.status !== "ended" ? (
        <Button
          size="small"
          color="error"
          startIcon={<StopCircleRoundedIcon />}
          aria-label={`End ${campaign.name}`}
          disabled={statusM.isPending}
          onClick={() => setEndCandidate(campaign)}
        >
          End
        </Button>
      ) : null}
    </Stack>
  );

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
            Promotions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {facilityName} · Create offers players can discover before checkout.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            setDuplicateSource(null);
            setStudioOpen(true);
          }}
          disabled={
            campaignsQ.isPending ||
            campaignsQ.isError ||
            contextQ.isPending ||
            contextQ.isError
          }
        >
          New promotion
        </Button>
      </Stack>

      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      <Box>
        <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Service offers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome new players, fill open capacity, or run a limited-time offer
          across courts, drop-in, and classes.
        </Typography>
      </Box>

      {campaignsQ.isPending || contextQ.isPending ? (
        <Stack role="status" spacing={1}>
          <Typography variant="body2">Loading promotions…</Typography>
          <Skeleton height={90} />
          <Skeleton height={90} />
        </Stack>
      ) : null}
      {campaignsQ.isError || contextQ.isError ? (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                void campaignsQ.refetch();
                void contextQ.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Promotion Studio could not be loaded.
        </Alert>
      ) : null}
      {contextQ.isSuccess && configuredServices.length === 0 ? (
        <Alert severity="warning">
          Configure one pricing currency for a supported service before
          publishing a promotion.
        </Alert>
      ) : null}
      {contextQ.isSuccess && waitingServices.length > 0 ? (
        <Alert severity="info">
          {waitingServices.map((item) => serviceLabel(item.service)).join(", ")}{" "}
          need setup before players can discover or redeem their offers. You can
          prepare promotions now; active or scheduled offers remain hidden until
          setup is complete.
        </Alert>
      ) : null}
      {campaignsQ.isSuccess && campaigns.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            No promotions yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start with a guided preset and preview exactly what players will
            see.
          </Typography>
        </Paper>
      ) : null}

      {campaigns.length > 0 && contextQ.data ? (
        compact ? (
          <Stack spacing={1.5}>
            {campaigns.map((campaign) => (
              <Paper
                key={campaign.campaign_id}
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Stack spacing={1.25}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
                      <Chip
                        size="small"
                        label={serviceLabel(campaign.service)}
                      />
                      <Typography variant="subtitle2">
                        {campaign.name}
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={
                        presentation(
                          campaign,
                          campaignServiceContext(
                            contextQ.data,
                            campaign.service,
                          )?.service_ready,
                        ).status
                      }
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {campaign.public
                      ? presentation(
                          campaign,
                          campaignServiceContext(
                            contextQ.data,
                            campaign.service,
                          )?.service_ready,
                        ).discovery
                      : "Private offer"}
                  </Typography>
                  {offer(campaign)}
                  <Typography variant="caption" color="text.secondary">
                    {schedule(campaign, contextQ.data.timezone)}
                  </Typography>
                  <Typography variant="caption">
                    {campaign.metrics
                      ? `${campaign.metrics.redemption_count} verified redemption${campaign.metrics.redemption_count === 1 ? "" : "s"} · ${formatMoney(campaign.metrics.customer_paid_amount, campaign.currency)} paid · ${formatMoney(campaign.metrics.net_paid_amount, campaign.currency)} net after refunds`
                      : "No verified redemptions yet"}
                  </Typography>
                  {campaign.audience === "earned_referral" ||
                  campaign.audience === "earned_loyalty" ? (
                    <Typography variant="caption" color="text.secondary">
                      {campaign.metrics?.pending_referral_count ?? 0} pending
                      referrals ·{" "}
                      {campaign.metrics?.available_reward_count ?? 0} available
                      rewards · {campaign.metrics?.redeemed_reward_count ?? 0}{" "}
                      redeemed
                    </Typography>
                  ) : null}
                  <Typography variant="caption">
                    {campaign.metrics
                      ? `${formatMoney(campaign.metrics.budget_remaining, campaign.currency)} budget remaining`
                      : `${formatMoney(campaign.spend_cap, campaign.currency)} budget cap`}{" "}
                    · BW fee{" "}
                    {formatMoney(
                      campaign.platform_fee_per_redemption,
                      campaign.currency,
                    )}{" "}
                    per redemption
                  </Typography>
                  {actions(campaign)}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Promotion contracts">
              <TableHead>
                <TableRow>
                  <TableCell>Promotion</TableCell>
                  <TableCell>Offer</TableCell>
                  <TableCell>Verified results</TableCell>
                  <TableCell>Budget & fee</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.campaign_id}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={serviceLabel(campaign.service)}
                        sx={{ mb: 0.5 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {campaign.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {campaign.public
                          ? presentation(
                              campaign,
                              campaignServiceContext(
                                contextQ.data,
                                campaign.service,
                              )?.service_ready,
                            ).discovery
                          : "Private offer"}
                      </Typography>
                    </TableCell>
                    <TableCell>{offer(campaign)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {campaign.metrics?.redemption_count ?? 0} redemptions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatMoney(
                          campaign.metrics?.customer_paid_amount ?? "0",
                          campaign.currency,
                        )}{" "}
                        paid ·{" "}
                        {formatMoney(
                          campaign.metrics?.refunded_amount ?? "0",
                          campaign.currency,
                        )}{" "}
                        refunded ·{" "}
                        {formatMoney(
                          campaign.metrics?.net_paid_amount ?? "0",
                          campaign.currency,
                        )}{" "}
                        net
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {formatMoney(
                          campaign.metrics?.booking_value_before_discounts ??
                            "0",
                          campaign.currency,
                        )}{" "}
                        booking value before discounts
                      </Typography>
                      {campaign.audience === "earned_referral" ||
                      campaign.audience === "earned_loyalty" ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {campaign.metrics?.pending_referral_count ?? 0}{" "}
                          pending referrals ·{" "}
                          {campaign.metrics?.available_reward_count ?? 0}{" "}
                          available ·{" "}
                          {campaign.metrics?.redeemed_reward_count ?? 0}{" "}
                          redeemed ·{" "}
                          {campaign.metrics?.corrected_reward_count ?? 0}{" "}
                          corrected/expired
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatMoney(campaign.spend_cap, campaign.currency)} cap
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatMoney(
                          campaign.metrics?.budget_remaining ??
                            campaign.spend_cap,
                          campaign.currency,
                        )}{" "}
                        remaining ·{" "}
                        {formatMoney(
                          campaign.platform_fee_per_redemption,
                          campaign.currency,
                        )}{" "}
                        / redemption
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {schedule(campaign, contextQ.data.timezone)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          presentation(
                            campaign,
                            campaignServiceContext(
                              contextQ.data,
                              campaign.service,
                            )?.service_ready,
                          ).status
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{actions(campaign)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : null}

      {contextQ.data ? (
        <PromotionStudioDialog
          open={studioOpen}
          context={contextQ.data}
          initialCampaign={duplicateSource}
          busy={createM.isPending}
          onClose={() => {
            setStudioOpen(false);
            setDuplicateSource(null);
          }}
          onCreate={create}
        />
      ) : null}
      <Dialog
        open={endCandidate != null}
        onClose={() => {
          if (!statusM.isPending) setEndCandidate(null);
        }}
        aria-labelledby="end-promotion-title"
      >
        <DialogTitle id="end-promotion-title">End promotion?</DialogTitle>
        <DialogContent>
          <Typography>
            {endCandidate?.name} will stop accepting new redemptions. This is
            irreversible; existing bookings, receipts, refunds, and recovery
            keep their original terms.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEndCandidate(null)}
            disabled={statusM.isPending}
          >
            Keep promotion
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={statusM.isPending || !endCandidate}
            onClick={async () => {
              if (!endCandidate) return;
              await changeStatus(endCandidate, "ended");
              setEndCandidate(null);
            }}
          >
            End promotion
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notice != null}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        message={notice ?? ""}
      />
    </Stack>
  );
}
